import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Clock, User, FileText, ArrowRight } from 'lucide-react';
import { format, formatDistanceToNow } from 'date-fns';
import { supabase } from '@/integrations/supabase/client';

interface StatusHistoryEntry {
  id: string;
  task_id: string;
  old_status: string | null;
  new_status: string;
  changed_by: string | null;
  changed_at: string;
  notes: string | null;
  user_name?: string;
}

interface TaskStatusHistoryProps {
  isOpen: boolean;
  onClose: () => void;
  taskId: string;
  taskTitle: string;
}

const getStatusColor = (status: string) => {
  switch (status) {
    case 'pending':
      return 'bg-yellow-100 text-yellow-800';
    case 'in-progress':
      return 'bg-blue-100 text-blue-800';
    case 'review':
      return 'bg-purple-100 text-purple-800';
    case 'on-hold':
      return 'bg-orange-100 text-orange-800';
    case 'completed':
      return 'bg-green-100 text-green-800';
    case 'cancelled':
      return 'bg-red-100 text-red-800';
    default:
      return 'bg-gray-100 text-gray-800';
  }
};

const getStatusIcon = (status: string) => {
  switch (status) {
    case 'pending':
      return '⏳';
    case 'in-progress':
      return '🔄';
    case 'review':
      return '👁️';
    case 'on-hold':
      return '⏸️';
    case 'completed':
      return '✅';
    case 'cancelled':
      return '❌';
    default:
      return '📋';
  }
};

const TaskStatusHistory: React.FC<TaskStatusHistoryProps> = ({
  isOpen,
  onClose,
  taskId,
  taskTitle,
}) => {
  const [history, setHistory] = useState<StatusHistoryEntry[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isOpen && taskId) {
      fetchStatusHistory();
    }
  }, [isOpen, taskId]);

  const fetchStatusHistory = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('task_status_history')
        .select(`
          *,
          users:changed_by (
            name
          )
        `)
        .eq('task_id', taskId)
        .order('changed_at', { ascending: false });

      if (error) {
        console.error('Error fetching status history:', error);
        return;
      }

      // Transform the data to include user names
      const transformedData = (data || []).map(entry => ({
        ...entry,
        user_name: entry.users?.name || 'System',
      }));

      setHistory(transformedData);
    } catch (error) {
      console.error('Error fetching status history:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatStatusLabel = (status: string | null) => {
    if (!status) return 'Created';
    return status.split('-').map(word => 
      word.charAt(0).toUpperCase() + word.slice(1)
    ).join(' ');
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[600px] max-h-[80vh]">
        <DialogHeader>
          <DialogTitle>Status History</DialogTitle>
          <p className="text-sm text-gray-600 mt-1">{taskTitle}</p>
        </DialogHeader>

        <ScrollArea className="h-[400px] pr-4">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
            </div>
          ) : history.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <Clock className="h-12 w-12 mx-auto mb-2 text-gray-300" />
              <p>No status changes recorded</p>
            </div>
          ) : (
            <div className="space-y-4">
              {history.map((entry, index) => (
                <div key={entry.id} className="relative">
                  {/* Connection line */}
                  {index < history.length - 1 && (
                    <div className="absolute left-5 top-10 bottom-0 w-0.5 bg-gray-200"></div>
                  )}

                  <div className="flex gap-4">
                    {/* Timeline dot */}
                    <div className="flex-shrink-0 w-10 h-10 rounded-full bg-white border-2 border-gray-300 flex items-center justify-center text-lg">
                      {getStatusIcon(entry.new_status)}
                    </div>

                    {/* Content */}
                    <div className="flex-1 pb-6">
                      <div className="bg-gray-50 rounded-lg p-4">
                        {/* Status change */}
                        <div className="flex items-center gap-2 mb-2">
                          {entry.old_status && (
                            <>
                              <Badge className={getStatusColor(entry.old_status)}>
                                {formatStatusLabel(entry.old_status)}
                              </Badge>
                              <ArrowRight className="h-4 w-4 text-gray-400" />
                            </>
                          )}
                          <Badge className={getStatusColor(entry.new_status)}>
                            {formatStatusLabel(entry.new_status)}
                          </Badge>
                        </div>

                        {/* Metadata */}
                        <div className="text-sm text-gray-600 space-y-1">
                          <div className="flex items-center gap-2">
                            <User className="h-3 w-3" />
                            <span>{entry.user_name}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <Clock className="h-3 w-3" />
                            <span>
                              {format(new Date(entry.changed_at), 'MMM d, yyyy h:mm a')}
                            </span>
                            <span className="text-gray-400">
                              ({formatDistanceToNow(new Date(entry.changed_at), { addSuffix: true })})
                            </span>
                          </div>
                          {entry.notes && (
                            <div className="flex items-start gap-2 mt-2">
                              <FileText className="h-3 w-3 mt-0.5" />
                              <span className="italic">{entry.notes}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}

              {/* Task creation entry */}
              <div className="relative">
                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-10 h-10 rounded-full bg-green-100 border-2 border-green-300 flex items-center justify-center text-lg">
                    🎯
                  </div>
                  <div className="flex-1">
                    <div className="bg-green-50 rounded-lg p-4">
                      <div className="mb-2">
                        <Badge className="bg-green-100 text-green-800">
                          Task Created
                        </Badge>
                      </div>
                      <div className="text-sm text-gray-600">
                        <div className="flex items-center gap-2">
                          <Clock className="h-3 w-3" />
                          <span>Task creation time not available</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
};

export default TaskStatusHistory;